import {
  RECORDING_ID_STORAGE_KEY,
  RECORDINGS_LIST_STORAGE_KEY,
  LOAD_MORE_ITEMS_MARGIN,
} from '../constants';
import { OPENER, QUERY_PARAM_OPENER } from '../../../../../features/Playbacks/constants/opener';
import type { State, Store } from '../../../types';

const onRecordingChanged = (state: State) => {
  const {
    site,
    lowerBound,
    recording,
    recordingId,
    recordingsList,
    rootRecording,
    hasMoreRecordings,
    populateRootRecording,
    fetchPreviousRecordingsPage,
    fetchNextRecordingsPage,
  } = state;

  // Temporary, required for FN-934
  if (!site) return;

  // Store current recording ID
  sessionStorage.setItem(RECORDING_ID_STORAGE_KEY, String(recordingId));

  // Not mid-loading, nothing to do
  if (!recording) return;

  // If we don't have any context on initial load, we fetch both previous and next pages
  if (!recordingsList.length) {
    if (rootRecording) populateRootRecording(recording);

    fetchPreviousRecordingsPage();
    fetchNextRecordingsPage();

    return;
  }

  const relativeIndex = recordingsList.findIndex((r) => r.id === recordingId);

  // If we're not in the first page and we're 3 recordings away from the limit, we fetch the previous page
  if (lowerBound && relativeIndex <= LOAD_MORE_ITEMS_MARGIN) {
    fetchPreviousRecordingsPage();
  }

  // If there are more recordings and we're 3 recordings away from the limit, we fetch the next page
  if (hasMoreRecordings && relativeIndex >= recordingsList.length - LOAD_MORE_ITEMS_MARGIN) {
    fetchNextRecordingsPage();
  }
};

export const setupFetchListOnRecordingChange = (store: Store): void => {
  const searchParams = new URLSearchParams(window.location.search);

  // Prevent list fetching when watching a public recording
  const token = searchParams.get('token');
  if (token) return;

  // Prevent list fetching when user comes from highlights, surveys or feedback
  const queryOpener = searchParams.get(QUERY_PARAM_OPENER) || '';
  const storedOpener = sessionStorage.getItem(QUERY_PARAM_OPENER) || '';

  // Persist opener configs on refresh
  if (queryOpener) {
    sessionStorage.setItem(QUERY_PARAM_OPENER, queryOpener);
  }

  const opener = queryOpener || storedOpener;
  if (
    [OPENER.HIGHLIGHT, OPENER.INCOMING_FEEDBACK_RESPONSE, OPENER.SURVEY_RESPONSE].includes(opener)
  ) {
    return;
  }

  const lsRecordingList = localStorage.getItem(RECORDINGS_LIST_STORAGE_KEY);
  const ssRecordingList = sessionStorage.getItem(RECORDINGS_LIST_STORAGE_KEY);

  /**
   * Workaround for RECS-1047
   */
  if (lsRecordingList || ssRecordingList) {
    if (lsRecordingList) {
      localStorage.removeItem(RECORDINGS_LIST_STORAGE_KEY);
      sessionStorage.setItem(RECORDINGS_LIST_STORAGE_KEY, lsRecordingList);
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const list: string = lsRecordingList || ssRecordingList;

    store.setState({ recordingsList: JSON.parse(list), lowerBound: 0, hasMoreRecordings: false });
    return;
  }

  // Listen to updates
  store.subscribe(
    onRecordingChanged,
    (state: State) => state,
    (state: State, newState: State) =>
      state.recording === newState.recording && state.site === newState.site,
  );
};
